import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, FileText, Printer, ChevronRight, ChevronLeft, Trash2, Eye } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { useInvoicePrint, InvoicePrintView } from './InvoicePrint';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Invoices = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const { handlePrint } = useInvoicePrint();

  const [invoices, setInvoices] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewInvoice, setViewInvoice] = useState(null);

  // ── Invoice from Deliveries dialog ────────────────────────────────────────
  const [isDNOpen, setIsDNOpen] = useState(false);
  const [isDNSubmitting, setIsDNSubmitting] = useState(false);
  const [dnStep, setDnStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedDNClient, setSelectedDNClient] = useState(null);
  const [uninvoicedNotes, setUninvoicedNotes] = useState([]);
  const [orderPrices, setOrderPrices] = useState({}); // { [order_id]: item_price }
  const [selectedDNIds, setSelectedDNIds] = useState(new Set());
  const [dnNextNumber, setDnNextNumber] = useState('');
  const [dnForm, setDnForm] = useState({
    invoice_type: 'invoice',
    subtotal: 0,
    vat_percentage: 14.0,
    apply_vat: true,
    discount: 0,
  });

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Delete this invoice?')) return;
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`, { headers: getAuthHeader() });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices`, { headers: getAuthHeader() });
      setInvoices(res.data);
    } catch {
      toast.error('Failed to fetch invoices');
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // ── Invoice from Deliveries dialog ────────────────────────────────────────
  const openDNDialog = async () => {
    setDnStep(1);
    setSelectedDNClient(null);
    setUninvoicedNotes([]);
    setOrderPrices({});
    setSelectedDNIds(new Set());
    setDnForm({ invoice_type: 'invoice', subtotal: 0, vat_percentage: 14.0, apply_vat: true, discount: 0 });
    try {
      const [clientsRes, numRes] = await Promise.all([
        axios.get(`${API}/clients`, { headers: getAuthHeader() }),
        axios.get(`${API}/invoices/next-number`, { headers: getAuthHeader() }),
      ]);
      setClients(clientsRes.data);
      setDnNextNumber(numRes.data.next_number);
    } catch {
      toast.error('Failed to initialize form');
    }
    setIsDNOpen(true);
  };

  const handleDNClientSelect = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedDNClient(client || null);
    setUninvoicedNotes([]);
    setOrderPrices({});
    setSelectedDNIds(new Set());
    if (!clientId) return;
    try {
      const [notesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/delivery-notes/uninvoiced?client_id=${clientId}`, { headers: getAuthHeader() }),
        axios.get(`${API}/orders?client_id=${clientId}`, { headers: getAuthHeader() }),
      ]);
      setUninvoicedNotes(notesRes.data);
      const priceMap = {};
      ordersRes.data.forEach(o => { priceMap[o.id] = o.item_price; });
      setOrderPrices(priceMap);
    } catch {
      toast.error('Failed to fetch delivery notes');
    }
  };

  const computeSubtotal = (selectedIds) => {
    let subtotal = 0;
    for (const dn of uninvoicedNotes) {
      if (selectedIds.has(dn.id)) {
        for (const item of (dn.items || [])) {
          const price = orderPrices[item.order_id] || 0;
          subtotal += item.quantity * price;
        }
      }
    }
    return subtotal;
  };

  const handleDNToggle = (dnId, checked) => {
    const next = new Set(selectedDNIds);
    if (checked) next.add(dnId); else next.delete(dnId);
    setSelectedDNIds(next);
    const subtotal = computeSubtotal(next);
    setDnForm(prev => ({ ...prev, subtotal }));
  };

  const dnVatAmount = dnForm.apply_vat
    ? (dnForm.subtotal - dnForm.discount) * (dnForm.vat_percentage / 100)
    : 0;
  const dnTotal = dnForm.subtotal - dnForm.discount + dnVatAmount;

  const handleDNSubmit = async () => {
    if (selectedDNIds.size === 0) { toast.error('Select at least one delivery note'); return; }
    if (isDNSubmitting) return;
    setIsDNSubmitting(true);
    const payload = {
      invoice_type: dnForm.invoice_type,
      delivery_note_ids: [...selectedDNIds],
      subtotal: dnForm.subtotal,
      vat_percentage: dnForm.apply_vat ? dnForm.vat_percentage : 0,
      vat_amount: dnVatAmount,
      discount: dnForm.discount,
      total: dnTotal,
      order_prices: orderPrices,
    };
    try {
      const res = await axios.post(`${API}/invoices/from-delivery-notes`, payload, { headers: getAuthHeader() });
      toast.success(`Invoice ${res.data.number} created`);
      setIsDNOpen(false);
      fetchInvoices();
    } catch {
      toast.error('Failed to create invoice');
    } finally {
      setIsDNSubmitting(false);
    }
  };

  return (
    <>
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('invoices')}</h2>
        <Dialog open={isDNOpen} onOpenChange={(open) => { if (!open) setIsDNOpen(false); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" onClick={openDNDialog} data-testid="invoice-from-dn-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>
                  {language === 'ar' ? 'فاتورة من إشعارات التسليم' : 'Invoice from Deliveries'}
                  {dnNextNumber && <span className="text-sm font-normal text-muted-foreground ml-2">— {dnNextNumber}</span>}
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                {(language === 'ar' ? ['العميل', 'إشعارات التسليم', 'التفاصيل'] : ['Client', 'Delivery Notes', 'Details']).map((label, i) => (
                  <React.Fragment key={i}>
                    <span className={dnStep === i + 1 ? 'font-semibold text-foreground' : ''}>{i + 1}. {label}</span>
                    {i < 2 && <ChevronRight className="w-3 h-3" />}
                  </React.Fragment>
                ))}
              </div>

              {/* DN Step 1: Select client */}
              {dnStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <Label>{language === 'ar' ? 'العميل' : 'Client'}</Label>
                    <select
                      value={selectedDNClient?.id || ''}
                      onChange={(e) => handleDNClientSelect(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">{language === 'ar' ? 'اختر عميل' : 'Select client'}</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  {selectedDNClient && uninvoicedNotes.length === 0 && (
                    <p className="text-sm text-muted-foreground">{language === 'ar' ? 'لا توجد إشعارات تسليم غير مفوترة لهذا العميل' : 'No uninvoiced delivery notes for this client.'}</p>
                  )}
                  <div className="flex justify-end">
                    <Button
                      onClick={() => setDnStep(2)}
                      disabled={!selectedDNClient || uninvoicedNotes.length === 0}
                      className="gap-2 bg-accent hover:bg-accent/90"
                    >
                      {language === 'ar' ? 'التالي' : 'Next'} <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* DN Step 2: Select delivery notes */}
              {dnStep === 2 && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'اختر إشعارات التسليم لتضمينها في هذه الفاتورة' : 'Select delivery notes to include in this invoice.'}</p>
                  <div className="space-y-3">
                    {uninvoicedNotes.map(dn => {
                      const orderNums = [...new Set((dn.items || []).map(i => i.order_number).filter(Boolean))].join(', ');
                      const totalQty = (dn.items || []).reduce((s, i) => s + i.quantity, 0);
                      const subtotalForDN = (dn.items || []).reduce((s, i) => s + i.quantity * (orderPrices[i.order_id] || 0), 0);
                      const hasNoPrice = (dn.items || []).every(i => !orderPrices[i.order_id]);
                      return (
                        <label key={dn.id} className="flex items-start gap-3 border border-border rounded-md p-3 cursor-pointer hover:bg-muted/30">
                          <input
                            type="checkbox"
                            checked={selectedDNIds.has(dn.id)}
                            onChange={(e) => handleDNToggle(dn.id, e.target.checked)}
                            className="w-4 h-4 mt-0.5"
                          />
                          <div className="flex-1 text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium">{dn.note_number}</span>
                              <span className="text-muted-foreground">{formatDate(dn.delivery_date)}</span>
                            </div>
                            {orderNums && <p className="text-muted-foreground">Orders: #{orderNums}</p>}
                            <p className="text-muted-foreground">Total qty: {totalQty} pcs</p>
                            {subtotalForDN > 0 && (
                              <p className="text-muted-foreground">Value: {subtotalForDN.toFixed(2)} {t('currency')}</p>
                            )}
                            {hasNoPrice && (
                              <p className="text-xs text-destructive mt-1">⚠ Order has no price set — edit the order to add Item Price</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setDnStep(1)} className="gap-2">
                      <ChevronLeft className="w-4 h-4" /> {language === 'ar' ? 'رجوع' : 'Back'}
                    </Button>
                    <Button
                      onClick={() => setDnStep(3)}
                      disabled={selectedDNIds.size === 0 || [...selectedDNIds].some(id => {
                        const dn = uninvoicedNotes.find(d => d.id === id);
                        return (dn?.items || []).every(i => !orderPrices[i.order_id]);
                      })}
                      className="gap-2 bg-accent hover:bg-accent/90"
                    >
                      {language === 'ar' ? 'التالي' : 'Next'} <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* DN Step 3: Financial details */}
              {dnStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <Label>{language === 'ar' ? 'النوع' : 'Type'}</Label>
                    <select
                      value={dnForm.invoice_type}
                      onChange={(e) => setDnForm(p => ({ ...p, invoice_type: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="invoice">{language === 'ar' ? 'فاتورة' : 'Invoice'}</option>
                      <option value="credit_note">{language === 'ar' ? 'إشعار دائن' : 'Credit Note'}</option>
                      <option value="debit_note">{language === 'ar' ? 'إشعار مدين' : 'Debit Note'}</option>
                    </select>
                  </div>
                  <div>
                    <Label>{language === 'ar' ? 'المجموع (ج.م)' : 'Subtotal (EGP)'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={dnForm.subtotal}
                      onChange={(e) => setDnForm(p => ({ ...p, subtotal: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>{language === 'ar' ? 'الخصم (ج.م)' : 'Discount (EGP)'}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={dnForm.discount}
                      onChange={(e) => setDnForm(p => ({ ...p, discount: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="dn-apply-vat"
                      checked={dnForm.apply_vat}
                      onChange={(e) => setDnForm(p => ({ ...p, apply_vat: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="dn-apply-vat">{language === 'ar' ? 'تطبيق ضريبة القيمة المضافة' : 'Apply VAT'}</Label>
                  </div>
                  {dnForm.apply_vat && (
                    <div>
                      <Label>{language === 'ar' ? 'نسبة ضريبة القيمة المضافة (%)' : 'VAT Percentage (%)'}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={dnForm.vat_percentage}
                        onChange={(e) => setDnForm(p => ({ ...p, vat_percentage: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                  )}
                  <div className="bg-muted/30 p-3 rounded-md space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>{language === 'ar' ? 'المجموع:' : 'Subtotal:'}</span>
                      <span className="font-mono-numeric">{dnForm.subtotal.toFixed(2)} {t('currency')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{language === 'ar' ? 'الخصم:' : 'Discount:'}</span>
                      <span className="font-mono-numeric">-{dnForm.discount.toFixed(2)} {t('currency')}</span>
                    </div>
                    {dnForm.apply_vat && (
                      <div className="flex justify-between">
                        <span>{language === 'ar' ? `ض.ق.م (${dnForm.vat_percentage}%):` : `VAT (${dnForm.vat_percentage}%):`}</span>
                        <span className="font-mono-numeric">{dnVatAmount.toFixed(2)} {t('currency')}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-base pt-2 border-t border-border">
                      <span>{t('total')}:</span>
                      <span className="font-mono-numeric">{dnTotal.toFixed(2)} {t('currency')}</span>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setDnStep(2)} className="gap-2">
                      <ChevronLeft className="w-4 h-4" /> {language === 'ar' ? 'رجوع' : 'Back'}
                    </Button>
                    <Button onClick={handleDNSubmit} disabled={isDNSubmitting} className="bg-accent hover:bg-accent/90" data-testid="dn-invoice-submit-btn">
                      {isDNSubmitting ? '...' : (language === 'ar' ? 'إنشاء فاتورة' : 'Create Invoice')}
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
      </div>

      {/* Filters */}
      {(() => {
        const uniqueClients = [...new Set(invoices.map(i => i.client_name).filter(Boolean))].sort();
        const filteredInvoices = invoices.filter(inv => {
          const matchesClient = !clientFilter || inv.client_name === clientFilter;
          const matchesFrom = !dateFrom || inv.date >= dateFrom;
          const matchesTo = !dateTo || inv.date <= dateTo;
          return matchesClient && matchesFrom && matchesTo;
        });
        return (
          <>
            <div className="flex flex-wrap gap-3 mb-4 items-end">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Client</label>
                <select className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
                  <option value="">All Clients</option>
                  {uniqueClients.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">From</label>
                <input type="date" className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">To</label>
                <input type="date" className="border border-border rounded px-2 py-1.5 text-sm bg-background"
                  value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </div>
              {(clientFilter || dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setClientFilter(''); setDateFrom(''); setDateTo(''); }}>
                  Clear
                </Button>
              )}
            </div>

            {/* Invoice list */}
            <div className="space-y-4">
              {filteredInvoices.map((invoice, idx) => {
          const typeLabel = invoice.invoice_type === 'credit_note' ? 'Credit Note'
            : invoice.invoice_type === 'debit_note' ? 'Debit Note' : 'Invoice';
          const refLabel = invoice.delivery_note_ids?.length > 0
            ? `From deliveries`
            : invoice.order_id ? `Order: #${invoice.order_id}` : '';
          return (
            <Card key={idx} className="p-4 border border-border hover:border-slate-300 transition-colors" data-testid={`invoice-card-${idx}`}>
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {typeLabel} {invoice.invoice_number}
                    </h3>
                    {refLabel && <p className="text-sm text-muted-foreground">{refLabel}</p>}
                    <p className="text-sm font-medium mt-1">{invoice.client_name}</p>
                  </div>
                  <div className="text-end">
                    <p className="text-sm text-muted-foreground">{formatDate(invoice.date)}</p>
                    <p className="text-xl font-bold font-mono-numeric mt-1">{invoice.total.toFixed(2)} {t('currency')}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm pt-2 border-t border-border">
                  <div>
                    <span className="text-muted-foreground">Subtotal:</span>
                    <p className="font-mono-numeric">{invoice.subtotal.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">VAT ({invoice.vat_percentage}%):</span>
                    <p className="font-mono-numeric">{invoice.vat_amount.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Discount:</span>
                    <p className="font-mono-numeric">{invoice.discount.toFixed(2)}</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-border flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setViewInvoice(invoice)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handlePrint(invoice)} className="gap-2">
                    <Printer className="w-4 h-4" />
                    Print
                  </Button>
                  {user?.role === 'admin' && (
                    <Button size="sm" variant="outline" onClick={() => handleDeleteInvoice(invoice.id)} className="gap-2 text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </Card>
              );
            })}
            </div>
            {filteredInvoices.length === 0 && (
              <div className="text-center py-12 text-muted-foreground" data-testid="empty-invoices">
                <p lang={language === 'ar' ? 'ar' : 'en'}>{invoices.length === 0 ? t('noInvoicesYet') : t('noInvoicesMatch')}</p>
              </div>
            )}
          </>
        );
      })()}
    </Card>

    {/* Invoice view modal */}
    <Dialog open={!!viewInvoice} onOpenChange={open => { if (!open) setViewInvoice(null); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {viewInvoice && <InvoicePrintView invoice={viewInvoice} />}
      </DialogContent>
    </Dialog>
    </>
  );
};
